import axios, { AxiosInstance, AxiosResponse, AxiosError } from "axios";

// Types for our logging API
interface LogRequest {
    level: "DEBUG" | "INFO" | "WARNING" | "ERROR" | "CRITICAL";
    message: string;
    component?: string;
    user_id?: string;
    session_id?: string;
    url?: string;
    user_agent?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extra?: Record<string, any>;
}

interface LogResponse {
    success: boolean;
    message: string;
    timestamp: string;
}

// Logger utility class for API errors
class APIErrorLogger {
    private sessionId: string;
    private baseUrl: string;

    constructor() {
        this.sessionId = this.generateSessionId();
        this.baseUrl = process.env.REACT_APP_API_URL || "";
    }

    private generateSessionId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    private getUserId(): string | undefined {
        // Try to get user ID from localStorage or other auth state
        try {
            const token = localStorage.getItem("accessToken");
            if (token) {
                // If you have a way to decode the JWT and get user ID, do it here
                // For now, we'll just indicate that user is authenticated
                return "authenticated_user";
            }
        } catch (error) {
            // Ignore errors when getting user ID
        }
        return undefined;
    }

    async logError(message: string, error: AxiosError, component = "api_client"): Promise<void> {
        try {
            const logData: LogRequest = {
                level: "ERROR",
                message,
                component,
                user_id: this.getUserId(),
                session_id: this.sessionId,
                url: window.location.href,
                user_agent: navigator.userAgent,
                extra: {
                    error_code: error.code,
                    status_code: error.response?.status,
                    status_text: error.response?.statusText,
                    request_url: error.config?.url,
                    request_method: error.config?.method?.toUpperCase(),
                    response_data: error.response?.data,
                    timestamp: new Date().toISOString(),
                },
            };

            // Use fetch to avoid circular dependency with axios
            await fetch(`${this.baseUrl}/api/logs/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(logData),
            });
        } catch (logError) {
            // If logging fails, fall back to console logging
            console.error("Failed to log API error to backend:", logError);
            console.error("Original API error:", error);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async logWarning(message: string, extra?: Record<string, any>, component = "api_client"): Promise<void> {
        try {
            const logData: LogRequest = {
                level: "WARNING",
                message,
                component,
                user_id: this.getUserId(),
                session_id: this.sessionId,
                url: window.location.href,
                user_agent: navigator.userAgent,
                extra: {
                    ...extra,
                    timestamp: new Date().toISOString(),
                },
            };

            await fetch(`${this.baseUrl}/api/logs/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(logData),
            });
        } catch (logError) {
            console.error("Failed to log warning to backend:", logError);
        }
    }
}

// Create a singleton logger instance
const errorLogger = new APIErrorLogger();

const axiosInstance: AxiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
});

// This is an interceptor that will run before each request to
//  add the access token to the Authorization header.
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("accessToken");
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error: AxiosError) => {
        // Log request configuration errors
        errorLogger.logError("Request interceptor error", error, "axios_request_interceptor");
        return Promise.reject(error);
    },
);

// This is an interceptor that will run before each response to check if the
//  response status is 401 (Unauthorized). If it is, the user will be redirected
//  to the login page.
axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const status = error.response?.status;

        if (status === 401) {
            // Don't log 401 errors as requested
            localStorage.removeItem("accessToken");
            window.location.href = "/login"; // Redirect to login page
        } else if (status === 403) {
            // Don't log 403 errors as requested
            console.warn("Access forbidden:", error.response?.data);
        } else {
            // Log all other API errors
            const errorMessage = `API Error: ${error.message}`;
            const requestUrl = error.config?.url || "unknown";
            const method = error.config?.method?.toUpperCase() || "unknown";

            await errorLogger.logError(
                `${errorMessage} - ${method} ${requestUrl}`,
                error,
                "axios_response_interceptor",
            );

            // Also log client-side network errors specifically
            if (error.code === "NETWORK_ERROR" || error.code === "ECONNABORTED") {
                await errorLogger.logWarning(
                    "Network connectivity issue detected",
                    {
                        error_type: "network_error",
                        error_code: error.code,
                        request_url: requestUrl,
                        request_method: method,
                    },
                    "network_monitor",
                );
            }
        }

        return Promise.reject(error);
    },
);

// Export the logger for use in other parts of the application
export { errorLogger, APIErrorLogger };
export type { LogRequest, LogResponse };
export default axiosInstance;
